import {Validators} from '@angular/forms';

import {ClientStatusesEnum, IndividualFilterModel, IndividualReportModel} from '@spa/api';

import {ComponentTypesEnum, UnspecifiedKeysEnum} from '../../app/shared/enums';
import {FilterControlInterface} from '../../app/shared/interfaces';
import {AutocompleteControl, BaseControl, Helper, SelectControl} from '../../app/shared/classes';
import {ReportTableColumn, ReportTableConfigInterface, ReportTableData} from '../../app/shared/components';

import {EnrollTypesEnum} from '../../app/entries/panel/enums';
import {EnrollDateSelector} from './panel-leads.descriptor';
import {OnboardingStageSelector, OnboardingStagesEnum} from './onboarding-stage';

export const PanelReportsMarketingStorageId = 'PanelReportsMarketingComponent';

export {OnboardingStageSelector, OnboardingStagesEnum, getStatusesForOnboardingStage} from './onboarding-stage';

export const PanelReportsMarketingFilterControls = (): FilterControlInterface[] => {
    return [
        {
            name: EnrollDateSelector,
            placeholder: 'Enroll date',
            controls: [
                new BaseControl({
                    name: IndividualFilterModel.getEnrollDateFromSelector(),
                    value: null,
                    placeholder: 'Enroll date from',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new BaseControl({
                    name: IndividualFilterModel.getEnrollDateToSelector(),
                    value: null,
                    placeholder: 'Enroll date to',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
            ],
            sortIndex: 1,
        },
        {
            name: IndividualFilterModel.getEnrollTypeSelector(),
            placeholder: 'Client Type',
            controls: [
                new SelectControl({
                    name: IndividualFilterModel.getEnrollTypeSelector(),
                    value: EnrollTypesEnum.All,
                    placeholder: 'Client Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: Helper.textsHelper().getEnrollTypeName(EnrollTypesEnum.All),
                            value: EnrollTypesEnum.All
                        },
                        {
                            label: Helper.textsHelper().getEnrollTypeName(EnrollTypesEnum.Individual),
                            value: EnrollTypesEnum.Individual
                        },
                        {
                            label: Helper.textsHelper().getEnrollTypeName(EnrollTypesEnum.Corporate),
                            value: EnrollTypesEnum.Corporate
                        },
                    ]
                })
            ],
            sortIndex: 3
        },
        {
            name: IndividualFilterModel.getNameSelector(),
            placeholder: 'Name',
            controls: [
                new BaseControl({
                    name: IndividualFilterModel.getNameSelector(),
                    value: null,
                    placeholder: 'Name',
                    validators: [Validators.minLength(3)],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            sortIndex: 4,
        },
        {
            name: IndividualFilterModel.getEmailSelector(),
            placeholder: 'Email',
            controls: [
                new BaseControl({
                    name: IndividualFilterModel.getEmailSelector(),
                    value: null,
                    placeholder: 'Email',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            sortIndex: 5,
        },
        {
            name: IndividualFilterModel.getCountryIdSelector(),
            placeholder: 'Country',
            controls: [
                new AutocompleteControl({
                    name: IndividualFilterModel.getCountryIdSelector(),
                    value: null,
                    placeholder: 'Country',
                    validators: [],
                    componentType: ComponentTypesEnum.Autocomplete,
                    options: []
                })
            ],
            sortIndex: 6
        },
        {
            name: IndividualFilterModel.getEnrollPortalIdsSelector(),
            placeholder: 'Portal',
            controls: [
                new SelectControl({
                    name: IndividualFilterModel.getEnrollPortalIdsSelector(),
                    value: UnspecifiedKeysEnum.UnspecifiedPortalIdKey,
                    placeholder: 'Portal',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label: 'All',
                            value: UnspecifiedKeysEnum.UnspecifiedPortalIdKey
                        }
                    ]
                })
            ],
            sortIndex: 7
        },
        {
            name: IndividualFilterModel.getEnrollTradingPlatformIdsSelector(),
            placeholder: 'Initial Platform',
            controls: [
                new SelectControl({
                    name: IndividualFilterModel.getEnrollTradingPlatformIdsSelector(),
                    value: UnspecifiedKeysEnum.UnspecifiedKey,
                    placeholder: 'Initial Platform',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    // Options are populated in the resolver from the trading platforms list.
                    options: [
                        {
                            label: 'All',
                            value: UnspecifiedKeysEnum.UnspecifiedKey
                        }
                    ]
                })
            ],
            sortIndex: 8
        },
        {
            name: IndividualFilterModel.getCurrentTradingPlatformIdsSelector(),
            placeholder: 'Current Platform',
            controls: [
                new SelectControl({
                    name: IndividualFilterModel.getCurrentTradingPlatformIdsSelector(),
                    value: null,
                    placeholder: 'Current Platform',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    // Multi-select; options are populated in the resolver from the trading
                    // platforms list. An empty selection means "all" (no filter applied).
                    options: [],
                    multiple: true
                })
            ],
            sortIndex: 9
        },
        {
            name: OnboardingStageSelector,
            placeholder: 'Onboarding Stage',
            controls: [
                new SelectControl({
                    name: OnboardingStageSelector,
                    value: OnboardingStagesEnum.All,
                    placeholder: 'Onboarding Stage',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {label: 'All', value: OnboardingStagesEnum.All},
                        {label: 'Leads', value: OnboardingStagesEnum.Leads},
                        {label: 'For Verification', value: OnboardingStagesEnum.ForVerification},
                        {label: 'Client', value: OnboardingStagesEnum.Client},
                    ]
                })
            ],
            sortIndex: 10
        },
        {
            name: IndividualFilterModel.getStatusesSelector(),
            placeholder: 'Status',
            controls: [
                new SelectControl({
                    name: IndividualFilterModel.getStatusesSelector(),
                    value: ClientStatusesEnum.All,
                    placeholder: 'Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    // Options are populated in the resolver from the status lookup collection.
                    options: []
                })
            ],
            sortIndex: 11
        }
    ];
};

export const PanelReportsMarketingDataTableItems = (): ReportTableData[] => {
    return [
        new ReportTableData<string>({
            colId: IndividualReportModel.getEnrollDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getVerificationDateSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: IndividualReportModel.getStatusGroupSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: IndividualReportModel.getStatusNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getEnrollTypeSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getEmailSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getResidentialCountryNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getResidentialCitySelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getEnrollPortalNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getEnrollTradingPlatformNameSelector(),
            value: null,
            hidden: false
        }),
        new ReportTableData<string>({
            colId: IndividualReportModel.getPlatformSubscriptionListSelector(),
            value: null,
            hidden: false
        })
    ];
};

export const PanelReportsMarketingDataTableConfig = (): ReportTableConfigInterface => {
    return {
        rows: [],
        columns: [
            new ReportTableColumn({
                label: 'Enroll Date',
                selector: IndividualReportModel.getEnrollDateSelector(),
                sortable: true
            }),
            new ReportTableColumn({
                label: 'For Verification Date',
                selector: IndividualReportModel.getVerificationDateSelector(),
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Onboarding Stage',
                selector: IndividualReportModel.getStatusGroupSelector()
            }),
            new ReportTableColumn({
                label: 'Status',
                selector: IndividualReportModel.getStatusNameSelector()
            }),
            new ReportTableColumn({
                label: 'Client Type',
                selector: IndividualReportModel.getEnrollTypeSelector(),
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Name',
                selector: IndividualReportModel.getNameSelector(),
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Email',
                selector: IndividualReportModel.getEmailSelector(),
                sortable: true
            }),
            new ReportTableColumn({
                label: 'Address Country',
                selector: IndividualReportModel.getResidentialCountryNameSelector()
            }),
            new ReportTableColumn({
                label: 'Address City',
                selector: IndividualReportModel.getResidentialCitySelector()
            }),
            new ReportTableColumn({
                label: 'Portal',
                selector: IndividualReportModel.getEnrollPortalNameSelector()
            }),
            new ReportTableColumn({
                label: 'Initial Platform',
                selector: IndividualReportModel.getEnrollTradingPlatformNameSelector()
            }),
            new ReportTableColumn({
                label: 'Current Platforms',
                selector: IndividualReportModel.getPlatformSubscriptionListSelector()
            }),
        ],
        hiddenColumnIds: [],
        actionMenu: false
    };
};
